import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { CORPORATE_DOCS_FAQ_CATEGORIES } from "@/content/corporate-docs-corpus/faq-defs";

export const Route = createFileRoute("/en/corporate-docs/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/corporate-docs-corpus");
    return { faqs: m.CORPORATE_DOCS_FAQ_EN };
  },
  component: CorporateDocsFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thai Corporate Documents & Cross-Border POA FAQ (${faqs.length} Answers)`,
        description:
          "Company affidavits from the DBD, board and shareholder resolutions, corporate powers of attorney, signing authority and the company seal, Notarial Services Attorney certification, MFA legalisation and the 2027 Apostille, subsidiaries and branches, KYC/UBO, trademark POAs and M&A documents.",
        path: "/en/corporate-docs/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/corporate-docs/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Corporate Documents FAQ", url: `${site}/en/corporate-docs/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/corporate-docs/faq")) },
      ],
    };
  },
});

function CorporateDocsFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Corporate Documents FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Thai Corporate Documents &amp; Cross-Border Powers of Attorney — {faqs.length} FAQs
        </h1>
        <p className="mb-6 text-muted-foreground">
          Answers follow the Thai Civil and Commercial Code, the Foreign Business Act B.E. 2542, the Revenue Code
          stamp-duty schedule, the Department of Business Development (dbd.go.th), the Department of Consular Affairs
          (consular.mfa.go.th), the Lawyers Council of Thailand and the Department of Intellectual Property
          (ipthailand.go.th). The Apostille Convention enters into force for Thailand on 28 February 2027. General
          information, not an official determination — confirm requirements with the receiving party before filing.
        </p>
        <section aria-label="Topics" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {CORPORATE_DOCS_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/corporate-docs/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <FaqSourceNote topic="corporate-docs" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}